import { FC, MouseEvent, useMemo, useState } from 'react';

import { AnalyticEventAction } from 'analytics';
import { trackActionButtonAnalyticsEvent } from 'analytics/events/action-button';
import { Dialogs } from 'analytics/events/dialog';
import { trackSectionUsageAnalyticsEvent } from 'analytics/events/section-usage';

import { observer } from 'mobx-react';

import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { sharedTestSelectors } from 'selectors/shared.selectors';
import { workQueueOtherPatientReportsSectionSelectors } from 'selectors/work-queue-other-patient-reports-section.selectors';


import { useStores } from 'mobx/hooks/useStores';

import { useTicketResolve } from 'hooks/useTicketResolve';

import { useWqOpenItemsSection } from 'hooks/useWqOpenItemsSection';

import { StyledSectionActionsContainer } from 'views/WorkQueue/WorkQueue.styled';
import { WorkQueueTabName } from 'views/WorkQueue/WorkQueue.types';

import { TicketSectionId } from 'views/WorkQueue/WorkQueue.types';

import WqCollapsibleSection from 'views/WorkQueue/WorkQueueOpenItemsView/WqCollapsibleSection';
import { WqSectionControl } from 'views/WorkQueue/WorkQueueOpenItemsView/WqSectionControl';
import { WqTicketPlaceholder } from 'views/WorkQueue/WqTicketPlaceholder';

import TicketReportRow from 'components/Ticket/TicketRow/TicketReportRow';
import { Section } from 'components/Ticket/TicketsContainers/TicketsContainers.constants';
import { OutlinedButton } from 'components/UIkit/atoms/Button';
import { MessageDialog } from 'components/UIkit/atoms/Dialog';

const WqPatientReportsSection: FC = () => {
  const { totalPages, totalItems, items, currentPage, isSectionPageLoading } =
    useWqOpenItemsSection(TicketSectionId.OtherPatientReports);
  const ticketResolve = useTicketResolve();
  const { workQueueStore } = useStores();
  const [isResolveAllLoading, setIsResolveAllLoading] = useState(false);
  const [isResolveAllPopupOpen, setIsResolveAllPopupOpen] = useState(false);

  const ticketsToResolve = useMemo(
    () =>
      workQueueStore.sectionDataMap[TicketSectionId.OtherPatientReports]?.items.map(
        (patientReportItem) => patientReportItem.itemData
      ),
    [workQueueStore.sectionDataMap]
  );
  const resolveAllTickets = async () => {
    if (ticketsToResolve) {
      await ticketResolve({ tickets: ticketsToResolve });
      workQueueStore.setSectionCurrentPage(TicketSectionId.OtherPatientReports, 0);
      workQueueStore.handleSectionPageChange(TicketSectionId.OtherPatientReports);
    }
  };

  const handleResolveAllClicked = async () => {
    trackActionButtonAnalyticsEvent({
      action: AnalyticEventAction.ResolveAll,
      value: workQueueStore.sectionDataMap[TicketSectionId.OtherPatientReports]?.totalItems,
      page_number: currentPage
    });
    setIsResolveAllLoading(true);

    try {
      await resolveAllTickets();
    } finally {
      setIsResolveAllLoading(false);
      setIsResolveAllPopupOpen(false);
    }
  };

  return (
    <>
      <MessageDialog
        id={Dialogs.ResolveAll}
        isOpen={isResolveAllPopupOpen}
        testHook={workQueueOtherPatientReportsSectionSelectors.resolveAllPatientReportsPopup}
        title={`Resolve ${
          ticketsToResolve?.length !== undefined ? ticketsToResolve.length + ' ' : ''
        }Patient Reports`}
        handleClose={() => setIsResolveAllPopupOpen(false)}
        primaryActionProps={{
          text: isResolveAllLoading ? 'Resolving...' : 'Resolve All',
          disabled: isResolveAllLoading,
          onClick: handleResolveAllClicked
        }}
        secondaryActionProps={{ text: 'Cancel', onClick: () => setIsResolveAllPopupOpen(false) }}
      >
        Are you sure you want to resolve all eligible reports in the current page of the “Other
        Patient Reports” section?
      </MessageDialog>

      <div className="report-section">
        <WqCollapsibleSection
          sectionName={Section.NonUrgent}
          totalItems={totalItems}
          onTrigger={(isOpen) =>
            trackSectionUsageAnalyticsEvent({
              action: isOpen ? AnalyticEventAction.Collapse : AnalyticEventAction.Expand,
              value: Section.NonUrgent,
              page_number: currentPage,
              tab: WorkQueueTabName.OpenItems
            })
          }
          testHook={sharedTestSelectors.ticketSection.container(
            TicketSectionId.OtherPatientReports
          )}
        >
          <StyledSectionActionsContainer>
            <OutlinedButton
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                setIsResolveAllPopupOpen(true);
              }}
              testHook={workQueueOtherPatientReportsSectionSelectors.resolveAllPatientReportsButton}
            >
              Resolve All
            </OutlinedButton>

            {totalPages > 1 && (
              <WqSectionControl sectionName={TicketSectionId.OtherPatientReports} />
            )}
          </StyledSectionActionsContainer>

          <div className="tickets-list">
            <TransitionGroup>
              {items.map(({ itemData }, index) => {
                const patient = workQueueStore.getPatientModel(itemData.patientId);
                const patientTicketReports =
                  patient?.getTicketReportsSortedBySeverity(itemData.id) ?? [];

                if (!isSectionPageLoading && !patientTicketReports?.length) return null;

                return (
                  <CSSTransition
                    key={itemData.id}
                    classNames="connect-ticket-transition"
                    timeout={600}
                  >
                    {isSectionPageLoading ? (
                      <WqTicketPlaceholder />
                    ) : (
                      <TicketReportRow
                        ticket={itemData}
                        reports={patientTicketReports}
                        withPatientLink
                        ticketIndex={index}
                        ticketSectionCurrentPage={currentPage - 1}
                        ticketSectionId={TicketSectionId.OtherPatientReports}
                      />
                    )}
                  </CSSTransition>
                );
              })}
            </TransitionGroup>
          </div>

          {totalPages > 1 && (
            <WqSectionControl sectionName={TicketSectionId.OtherPatientReports} isBottom />
          )}
        </WqCollapsibleSection>
      </div>
    </>
  );
};

export default observer(WqPatientReportsSection);
